/**
 * Shared behaviour for themed video players (UGC cards, image/video banner).
 *
 * Two jobs, both declarative so the banner section and the carousel can share
 * them without importing across scripts:
 *
 * 1. Keep WebVTT cues clear of the controls overlaid on the video. Native cues
 *    sit on the bottom edge, which is exactly where our controls live.
 * 2. Report whether a clip actually carries an audio track, so an owning
 *    component can withhold a mute control from silent footage.
 *
 * Markup contract:
 *   [data-video-a11y]      on the <video>
 *   [data-video-controls]  the overlay whose top edge cues must stay above
 *   [data-detect-audio]    optional opt-in to automatic track detection
 *
 * A positive audio finding is published as `data-has-audio="true"` plus a
 * bubbling `theme:video:audio` event. Nothing is published for silent or
 * undecided clips, so components can start hidden and only reveal sound
 * controls after the browser has positively identified an audio track.
 */
(() => {
  // This asset is included by each video section. The symbol keeps execution
  // idempotent when a page contains more than one of those sections, while
  // still letting later instances initialise any newly rendered videos.
  const STATE_KEY = Symbol.for('meaco.videoAccessibility');
  const existingInit = window[STATE_KEY];
  if (existingInit) {
    existingInit();
    return;
  }

  const AUDIO_EVENT = 'theme:video:audio';

  // Distance to leave between the caption box and the controls.
  const CUE_GAP = 8;

  // Fallback when the controls cannot be measured (hidden, or not yet laid out).
  const CUE_FALLBACK_LINE = 88;

  // Last line applied per track, so a resize that changes nothing does not
  // interrupt a cue that is currently on screen.
  const appliedLines = new WeakMap();

  // Keep binding state in JavaScript rather than a data attribute: Splide
  // copies attributes when it creates loop clones. A Map also lets refreshes
  // release observers held by clones that Splide has removed.
  const videoBindings = new Map();

  const captionTracks = (video) =>
    [...video.textTracks].filter((track) => track.kind === 'captions' || track.kind === 'subtitles');

  // Walk up from the video so a carousel card finds its own controls rather
  // than a neighbouring card's. The video's parent already contains them.
  const findControls = (video) => {
    let node = video.parentElement;

    while (node && node !== document.body) {
      const controls = node.querySelector('[data-video-controls]');
      if (controls) return controls;
      node = node.parentElement;
    }

    return null;
  };

  const cueLineFor = (video) => {
    const controls = findControls(video);
    const videoBox = video.getBoundingClientRect();

    if (!controls || controls.hidden || !videoBox.height) return CUE_FALLBACK_LINE;

    const controlsBox = controls.getBoundingClientRect();
    if (!controlsBox.height) return CUE_FALLBACK_LINE;

    const available = controlsBox.top - videoBox.top - CUE_GAP;
    const line = (available / videoBox.height) * 100;

    return Math.max(20, Math.min(95, line));
  };

  const positionCues = (video) => {
    const line = Math.round(cueLineFor(video));

    for (const track of captionTracks(video)) {
      if (!track.cues || !track.cues.length) continue;
      if (appliedLines.get(track) === line) continue;

      let positionedCue = false;

      for (const cue of track.cues) {
        try {
          if ('snapToLines' in cue) cue.snapToLines = false;
          if ('lineAlign' in cue) cue.lineAlign = 'end';
          cue.line = line;
          positionedCue = true;
        } catch {
          // Older engines expose cues whose positioning fields are read-only.
        }
      }

      if (positionedCue) appliedLines.set(track, line);
    }
  };

  /**
   * Browsers disagree on how to expose an audio track, and none of the signals
   * are available on every engine, so treat this as a tri-state.
   *
   * @returns {'yes'|'unknown'}
   */
  const detectAudio = (video) => {
    // Safari and Firefox can positively identify a track from metadata. An
    // empty list is not accepted as proof of silence: Chromium exposes an empty
    // audioTracks list even when it later decodes audio.
    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      const tracks = video.audioTracks;
      if (tracks && typeof tracks.length === 'number' && tracks.length > 0) return 'yes';

      if (video.mozHasAudio === true) return 'yes';
    }

    // Chromium only reveals a positive signal through decoder counters, which
    // stay at zero until playback starts. Video bytes can arrive before audio
    // bytes, so their presence must not settle the answer as silent.
    if (typeof video.webkitAudioDecodedByteCount === 'number') {
      if (video.webkitAudioDecodedByteCount > 0) return 'yes';
    }

    return 'unknown';
  };

  const probeAudio = (video) => {
    if (video.dataset.hasAudio) return true;

    const state = detectAudio(video);
    if (state === 'unknown') return false;

    video.dataset.hasAudio = 'true';
    video.dispatchEvent(new CustomEvent(AUDIO_EVENT, { bubbles: true, detail: { hasAudio: true } }));

    return true;
  };

  const observe = (video) => {
    if (videoBindings.has(video)) return;

    const syncCues = () => positionCues(video);
    const shouldDetectAudio = video.hasAttribute('data-detect-audio');

    const onLoadedMetadata = () => {
      syncCues();
      if (shouldDetectAudio) probeAudio(video);
    };
    const onPlaying = () => probeAudio(video);

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    if (shouldDetectAudio) video.addEventListener('playing', onPlaying);

    // Chromium needs a moment of playback before its counters move, so keep
    // sampling until the question is settled.
    const onTimeUpdate = () => {
      if (probeAudio(video)) video.removeEventListener('timeupdate', onTimeUpdate);
    };

    if (shouldDetectAudio) video.addEventListener('timeupdate', onTimeUpdate);

    const trackElements = [...video.querySelectorAll('track')];
    for (const track of trackElements) {
      track.addEventListener('load', syncCues);
    }

    let resizeObserver = null;
    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(syncCues);
      resizeObserver.observe(video);
    }

    videoBindings.set(video, () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('timeupdate', onTimeUpdate);
      for (const track of trackElements) track.removeEventListener('load', syncCues);
      resizeObserver?.disconnect();
    });

    syncCues();
    if (shouldDetectAudio) probeAudio(video);
  };

  const release = (video) => {
    videoBindings.get(video)?.();
    videoBindings.delete(video);
  };

  const init = () => {
    for (const video of videoBindings.keys()) {
      if (!video.isConnected) release(video);
    }

    for (const video of document.querySelectorAll('video[data-video-a11y]')) observe(video);
  };

  window[STATE_KEY] = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Section rerenders in the theme editor bring in fresh video elements, and a
  // carousel only builds its loop clones once it mounts.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    for (const video of event.target.querySelectorAll('video[data-video-a11y]')) release(video);
  });
  document.addEventListener('theme:video:refresh', init);
})();
